import {
	Fragment,
	type Mark,
	type Node as PMNode,
	Slice,
} from '@atlaskit/editor-prosemirror/model';
import { AddMarkStep, RemoveMarkStep, ReplaceStep } from '@atlaskit/editor-prosemirror/transform';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';

function isStructural(step: ReplaceStep): boolean {
	return (step as ReplaceStep & { structure?: boolean }).structure === true;
}

/**
 * Coalesces adjacent compatible steps in a single pass:
 *   - consecutive non-structural ReplaceSteps that abut (last.to === next.from)
 *   - overlapping/adjacent AddMarkSteps with the same mark
 *   - overlapping/adjacent RemoveMarkSteps with the same mark
 *
 * Mirrors `StepUtils.mergeSteps`. One pass only; call again if you suspect
 * a second-round merge is possible.
 */
export function mergeSteps(steps: readonly Step[]): Step[] {
	if (steps.length === 0) return [];
	const out: Step[] = [steps[0]];

	for (let i = 1; i < steps.length; i++) {
		const next = steps[i];
		const last = out[out.length - 1];

		if (
			last instanceof ReplaceStep &&
			next instanceof ReplaceStep &&
			!isStructural(last) &&
			!isStructural(next) &&
			last.to === next.from &&
			// All four opens must be 0: the merged slice is built `Slice(_, 0, 0)`,
			// so a non-zero open on either side would either misclaim the outer
			// opens or reinterpret an open-at-junction node as complete.
			last.slice.openStart === 0 &&
			last.slice.openEnd === 0 &&
			next.slice.openStart === 0 &&
			next.slice.openEnd === 0
		) {
			out[out.length - 1] = new ReplaceStep(
				last.from,
				next.to,
				mergeSlices(last.slice, next.slice),
			);
			continue;
		}

		if (
			last instanceof AddMarkStep &&
			next instanceof AddMarkStep &&
			last.mark.eq(next.mark) &&
			last.from <= next.to &&
			last.to >= next.from
		) {
			out[out.length - 1] = new AddMarkStep(
				Math.min(last.from, next.from),
				Math.max(last.to, next.to),
				next.mark,
			);
			continue;
		}

		if (
			last instanceof RemoveMarkStep &&
			next instanceof RemoveMarkStep &&
			last.mark.eq(next.mark) &&
			last.from <= next.to &&
			last.to >= next.from
		) {
			out[out.length - 1] = new RemoveMarkStep(
				Math.min(last.from, next.from),
				Math.max(last.to, next.to),
				next.mark,
			);
			continue;
		}

		out.push(next);
	}
	return out;
}

function mergeSlices(a: Slice, b: Slice): Slice {
	const aContent = a.content;
	const bContent = b.content;
	if (aContent.size === 0) return new Slice(bContent, 0, 0);
	if (bContent.size === 0) return new Slice(aContent, 0, 0);

	const aLast = aContent.child(aContent.childCount - 1);
	const bFirst = bContent.child(0);

	if (aLast.isText && bFirst.isText && sameMarkSet(aLast.marks, bFirst.marks)) {
		const merged = aLast.type.schema.text((aLast.text ?? '') + (bFirst.text ?? ''), aLast.marks);
		const aHead = Fragment.from(takeAll(aContent, aContent.childCount - 1));
		const bTail = Fragment.from(takeAllFrom(bContent, 1));
		return new Slice(aHead.append(Fragment.from(merged)).append(bTail), 0, 0);
	}
	return new Slice(aContent.append(bContent), 0, 0);
}

function sameMarkSet(a: readonly Mark[], b: readonly Mark[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((m) => b.some((n) => n.eq(m)));
}

function takeAll(frag: Fragment, n: number): PMNode[] {
	const out: PMNode[] = [];
	for (let i = 0; i < n; i++) out.push(frag.child(i));
	return out;
}

function takeAllFrom(frag: Fragment, start: number): PMNode[] {
	const out: PMNode[] = [];
	for (let i = start; i < frag.childCount; i++) out.push(frag.child(i));
	return out;
}
